/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable indent */
'use client';

import { useState, useEffect, Suspense, useCallback } from 'react';
import { motion } from 'framer-motion';
import PropertyFilters from './property-filters';
import PropertyGrid from './property-grid';
import PropertyListHeader from './property-list-header';
import { useSearchParams, useRouter } from 'next/navigation';
import { PropertyService } from '@/app/components/property-service';
import Loading from '@/app/loading';

export default function PropertyListingPage({
  offeringType,
  title,
  description,
}) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalProperties, setTotalProperties] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const itemsPerPage = 9;

  const [viewMode, setViewMode] = useState('grid');

  const propertyType = searchParams.get('propertyType') || '';
  const minPrice = searchParams.get('minPrice') || '0';
  const maxPrice = searchParams.get('maxPrice') || '100000000';
  const bedrooms = searchParams.get('bedrooms') || '';
  const bathrooms = searchParams.get('bathrooms') || '';
  const refNo = searchParams.get('refNo') || '';
  const sortBy = searchParams.get('sortBy') || 'newest';
  const rawLocation = (searchParams.get('location') || '').trim();
  const searchLocation = (searchParams.get('q') || '').trim();
  const location = searchLocation || rawLocation;

  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);
      const pfType = offeringType === 'off-plan' ? 'offplan' : offeringType;
      const data = await PropertyService.getPFProperties(pfType);
      setProperties(data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  }, [offeringType]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    if (!properties.length) return;

    const filtered = properties.filter((property) => {
      // All properties come from PF — match using pre-classified listingSection
      const pfSectionToUrlType = { buy: 'buy', rent: 'rent', offplan: 'off-plan' };
      const offeringTypeMatch = pfSectionToUrlType[property.listingSection] === offeringType;

      const propertyTypeMatch =
        !propertyType || property.property_type?.[0] === propertyType;

      const price = parseFloat(property.price?.[0]);
      const priceMatch =
        price >= parseFloat(minPrice) && price <= parseFloat(maxPrice);

      // const bedroomMatch = !bedrooms
      //   ? true
      //   : bedrooms === '0'
      //     ? property.bedroom?.[0] === '0' || !property.bedroom?.[0]
      //     : bedrooms === '6+'
      //       ? parseInt(property.bedroom?.[0] || '0') >= 6
      //       : property.bedroom?.[0] === bedrooms;

      // const bathroomsMatch = !bathrooms
      //   ? true
      //   : bathrooms === '0'
      //     ? bathroomValue === '0' || bathroomValue === ''
      //     : bathrooms.endsWith('+')
      //       ? (() => {
      //           const min = parseInt(bathrooms, 10);

      //           const allowed = [];

      //           for (let i = min; i <= 10; i++) {
      //             allowed.push(i.toString(), `${i}+`);
      //           }

      //           return allowed.includes(bathroomValue);
      //         })()
      //       : bathroomValue === bathrooms;

      const bedroomArray = bedrooms ? bedrooms.split(',') : [];
      const bathroomArray = bathrooms ? bathrooms.split(',') : [];

      const bedroomMatch =
        bedroomArray.length === 0
          ? true
          : bedroomArray.some((bed) => {
              if (bed === '6+') {
                return parseInt(property.bedroom?.[0] || '0') >= 6;
              }
              if (bed === '0') {
                return property.bedroom?.[0] === '0' || !property.bedroom?.[0];
              }
              return property.bedroom?.[0] === bed;
            });

      const bathroomsMatch =
        bathroomArray.length === 0
          ? true
          : bathroomArray.some((bath) => {
              const value = property.bathroom?.[0] || '';

              if (bath.endsWith('+')) {
                const min = parseInt(bath, 10);
                return parseInt(value || '0') >= min;
              }

              return value === bath;
            });

      const locationMatch = !location
        ? true
        : (property.community?.[0] &&
            property.community[0]
              .toLowerCase()
              .includes(location.toLowerCase())) ||
          (property.sub_community?.[0] &&
            property.sub_community[0]
              .toLowerCase()
              .includes(location.toLowerCase()));

      const refNoMatch = !refNo
        ? true
        : property.reference_number?.[0] &&
          property.reference_number?.[0].includes(refNo);

      return (
        offeringTypeMatch &&
        propertyTypeMatch &&
        priceMatch &&
        bedroomMatch &&
        bathroomsMatch &&
        locationMatch &&
        refNoMatch
      );
    });

    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'price-asc') {
        return parseFloat(a.price) - parseFloat(b.price);
      }
      if (sortBy === 'price-desc') {
        return parseFloat(b.price) - parseFloat(a.price);
      }
      return (
        new Date(b.created_at || 0).getTime() -
        new Date(a.created_at || 0).getTime()
      );
    });

    setFilteredProperties(sorted);
    setTotalProperties(sorted.length);
  }, [
    properties,
    offeringType,
    propertyType,
    minPrice,
    maxPrice,
    bedrooms,
    bathrooms,
    location,
    refNo,
    sortBy,
  ]);

  const getCurrentPageProperties = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProperties.slice(startIndex, endIndex);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Delay scroll to top slightly so it doesn’t happen while loading screen is active
    // setTimeout(() => {
    //   window.scrollTo({ top: 0, behavior: 'smooth' });
    // }, 100); // or 300ms if your Loading stays for a bit
  };

  const updateFilters = (filters) => {
    const params = new URLSearchParams();

    if (filters.propertyType) params.set('propertyType', filters.propertyType);
    if (filters.minPrice && filters.minPrice !== '0')
      params.set('minPrice', filters.minPrice);
    if (filters.maxPrice && filters.maxPrice !== '100000000')
      params.set('maxPrice', filters.maxPrice);
    if (filters.bedrooms) params.set('bedrooms', filters.bedrooms);
    if (filters.bathrooms) params.set('bathrooms', filters.bathrooms);
    if (filters.location) params.set('location', filters.location);
    if (filters.refNo) params.set('refNo', filters.refNo);
    if (filters.sortBy && filters.sortBy !== 'newest')
      params.set('sortBy', filters.sortBy);

    router.push(`/properties/${offeringType}?${params.toString()}`);
  };

  // if (loading) return <Loading />;

  if (error)
    return (
      <div className='container mx-auto px-4 py-16 text-center text-red-600'>
        {error}
      </div>
    );

  return (
    <>
      <h2 className='sr-only'>
        X-Space Real Estate Broker | Properties for Sale
      </h2>
      {loading && <Loading />} {/* Shows loader as overlay only */}
      <div className={loading ? 'pointer-events-none blur-sm' : ''}>
        <Suspense fallback={<Loading />}>
          <div className='bg-gray-50 dark:bg-gray-900 min-h-screen pt-24 py-20'>
            <div className='container mx-auto px-4'>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className='mb-8'
              >
                <PropertyListHeader
                  title={title}
                  description={description}
                  totalProperties={totalProperties}
                  isFilterOpen={isFilterOpen}
                  setIsFilterOpen={setIsFilterOpen}
                  viewMode={viewMode}
                  setViewMode={setViewMode}
                />
              </motion.div>

              <div className='flex flex-col lg:flex-row gap-8'>
                <motion.div
                  className={`fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden ${isFilterOpen ? 'block' : 'hidden'}`}
                  initial={false}
                  animate={{ opacity: isFilterOpen ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                  onClick={() => setIsFilterOpen(false)}
                />

                <motion.div
                  className={`fixed top-0 left-0 h-full w-80 bg-white dark:bg-gray-800 z-50 overflow-auto lg:hidden transform ${
                    isFilterOpen ? 'translate-x-0' : '-translate-x-full'
                  }`}
                  initial={false}
                  animate={{ x: isFilterOpen ? 0 : '-100%' }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  <div className='p-4'>
                    <button
                      onClick={() => setIsFilterOpen(false)}
                      className='absolute top-4 right-4 text-gray-500 hover:text-gray-700'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        className='h-6 w-6'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M6 18L18 6M6 6l12 12'
                        />
                      </svg>
                    </button>
                    <h3 className='text-xl font-bold mb-6 mt-2'>Filters</h3>
                    <PropertyFilters
                      initialFilters={{
                        propertyType,
                        minPrice,
                        maxPrice,
                        bedrooms,
                        bathrooms,
                        location,
                        refNo,
                        sortBy,
                      }}
                      onFilterChange={updateFilters}
                      onClose={() => setIsFilterOpen(false)}
                    />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className='hidden lg:block lg:w-1/4 xl:w-2/6'
                >
                  <div className='bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 sticky top-24'>
                    <h3 className='text-xl font-bold mb-6'>Filters</h3>
                    <PropertyFilters
                      initialFilters={{
                        propertyType,
                        minPrice,
                        maxPrice,
                        bedrooms,
                        bathrooms,
                        location,
                        refNo,
                        sortBy,
                      }}
                      onFilterChange={updateFilters}
                    />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className='lg:w-3/4 xl:w-4/5'
                >
                  <PropertyGrid
                    properties={getCurrentPageProperties()}
                    totalProperties={totalProperties}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    offeringType={offeringType}
                    viewMode={viewMode}
                  />
                </motion.div>
              </div>
            </div>
          </div>
        </Suspense>
      </div>
    </>
  );
}
